import React from 'react';

import Seo from '../components/Seo';
import { getTranslationText, l10nContext } from '../utils/l10nContext';

import type { PageProps } from 'gatsby';
import Fonts from '../components/Fonts';
import type { LanguageType } from '../utils/l10nContext';

type PropsMerge<T, U> = Omit<T, keyof U> & U;

type LayoutProps = PropsMerge<
  PageProps<
    GatsbyTypes.MainPageQuery,
    {
      language: string;
      titleImagePath: string;
      buttonImagePath: string;
      pathname: string;
      game: string;
      subGame: string;
      backgroundColor?: string;
    }
  >,
  { children?: React.ReactNode }
>;

const RootLayout: React.FC<LayoutProps> = (props) => {
  const { data, children, pageContext } = props;
  const lang = pageContext?.language ?? 'en';
  type L10nContext = React.ContextType<typeof l10nContext>;
  const l10n = React.useMemo<L10nContext>(() => {
    if (!data?.pageTarget?.translations) {
      return null;
    }
    const language = data.pageTarget.language as LanguageType;
    const messages = Object.fromEntries(data.pageTarget.translations.entries.map(({ key, value }) => [key, value]));
    if (!(language && messages)) {
      return null;
    }
    return {
      language,
      messages,
      t: (key, components) => getTranslationText({ language, messages }, key, components),
    };
  }, [data]);
  const subGame = pageContext.subGame;
  const gameCode = data?.pageTarget?.pathname.includes('kingdom') ? 'ck' : 'cro';
  const t = React.useCallback<NonNullable<L10nContext>['t']>(
    (key) => {
      if (!l10n) {
        window.location.href = '/';
        return 'Localization data not found';
      }
      return l10n.t(key);
    },
    [l10n],
  );
  return (
    <l10nContext.Provider value={l10n}>
      <Fonts lang={lang} />

      {pageContext.language && (
        <Seo
          lang={lang}
          title={t('devplayguide_meta_title') as string}
          description={
            (gameCode
              ? t(`devplayguide_meta_description_${pageContext.game}-${subGame}`)
              : t('devplayguide_meta_title')) as string
          }
          game={pageContext.game}
        />
      )}
      {children}
    </l10nContext.Provider>
  );
};

export default RootLayout;
